import * as React from 'react'
import querystring from 'querystring'
import { useMutation } from 'react-apollo'
import { ApolloError } from 'apollo-client'
import { useFormState } from 'react-use-form-state'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { isEmpty } from 'lodash'

import Error from '../../../../components/error'
import Button from '../../../../components/button'
import SubTitle from '../../../../components/sub-title'
import BankidCard from '../../../../components/bankid-card'
import { getValidity, Text, Toggle } from '../../../../components/form'

import {
  getEnv,
  pushGAEvent,
  GAEventType,
  validateSvensktOrganisationsnummer,
  normalizeSvensktOrganisationsnummer,
} from '../../../../lib/utils'
import ApolloErrorMessage from '../../../../components/apollo-error-message'

import BankidSessionStatus, {
  getBankidSessionStatusMessage,
} from '../../../../components/functional/basic-signup/bankid-session'
import { startBankIdSession } from '../../../../components/functional/basic-signup/bankid-session/gql'
import {
  DooerComSignup,
  DooerComSignupVariables,
} from '../../../../components/functional/basic-signup/bankid-session/__generated__/DooerComSignup'
import { DooerComGetBankIdSessionV2_bankIdSessionV2 } from '../../../../components/functional/basic-signup/bankid-session/__generated__/DooerComGetBankIdSessionV2'

import messages from '../../../../components/functional/basic-signup/form/messages'
import styles from '../../../../components/functional/basic-signup/form/form.module.scss'

import FreeHandwriting from '../../../free/components/free-handwriting'

import st from './signupForm.module.scss'

export interface IFormValues {
  email: string
  integrity: boolean
  termsOfService: boolean
  companyName: string
  orgNr: number
}

interface IFormProps {
  onAfterSubmit?: () => void
  initialValues?: IFormValues
  apps?: string[]
  event?: GAEventType
}

const msgs = defineMessages({
  labelFindCompany: {
    id: 'diy-accounting.signup-form.labels.find-company',
    defaultMessage: 'Sök företag med namn eller org.nr.*',
  },
  submit: {
    id: 'diy-accounting.signup-form.labels.submit',
    defaultMessage: 'Kom igång - det är gratis',
  },
  submitShort: {
    id: 'diy-accounting.signup-form.labels.submit-short',
    defaultMessage: 'Skapa konto',
  },
  arrowText: {
    id: 'diy-accounting.signup-form.arrow-text',
    defaultMessage: `0 kr`,
  },
})

const isGraphqlError = (value: unknown): value is ApolloError => {
  const t = value as ApolloError
  return t && typeof t === 'object' && Array.isArray(t.graphQLErrors)
}

function Form({ onAfterSubmit, initialValues, apps = [], event = 'signup' }: IFormProps) {
  const intl = useIntl()
  const [mutate, { loading, error }] = useMutation<DooerComSignup, DooerComSignupVariables>(startBankIdSession)

  const errorMessages = {
    email: intl.formatMessage(messages.errorEmail),
    emailMismatch: intl.formatMessage(messages.errorEmailMismatch),
    integrity: intl.formatMessage(messages.errorIntegrity),
    termsOfService: intl.formatMessage(messages.errorTermsOfService),
    text: intl.formatMessage(messages.errorText),
    cellPhone: intl.formatMessage(messages.errorCellPhone),
    organizationNumber: intl.formatMessage(messages.errorOrganizationNumber),
    companyName: intl.formatMessage(messages.errorShortCompanyName),
  }

  const [bankidSessionId, setBankidSessionId] = React.useState<string | undefined>()
  const [bankidSessionData, setBankidSessionData] = React.useState<
    DooerComGetBankIdSessionV2_bankIdSessionV2 | undefined
  >()
  const [bankidError, setBankidError] = React.useState<ApolloError>()
  const [autoStartToken, setAutoStartToken] = React.useState<string | null>()
  const [bankIdQRImage, setQR] = React.useState<string | null>()
  const [formState, input] = useFormState<IFormValues>(initialValues, {
    withIds: true,
  })

  const stringifiedFormData = querystring.stringify(formState.values)

  const bankIdRef = React.createRef<HTMLDivElement>()
  const isSessionIdInUrl = window.location.search.toLowerCase().includes('bankidsessionid')

  React.useEffect(() => {
    if (isSessionIdInUrl) {
      const params = querystring.parse(window.location.search.replace(/^\?/, ''))

      if (params.bankidsessionid && typeof params.bankidsessionid === 'string') {
        setBankidSessionId(params.bankidsessionid)
      }
    }
  }, [isSessionIdInUrl])

  const redirectUrl =
    window.location.origin + window.location.pathname + `?bankidsessionid=${bankidSessionId}&${stringifiedFormData}`

  const apolloError = error || bankidError

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        if (Object.keys(formState.errors).length > 0) {
          return false
        }

        mutate({
          variables: {
            sePersonnummer: null,
            input: {
              companyName: formState.values.companyName.trim(),
              email: formState.values.email.trim(),
              firstName: '',
              lastName: '',
              organizationNumber: normalizeSvensktOrganisationsnummer(formState.values.orgNr.trim()),
              apps,
            },
          },
        }).then((res) => {
          if (res && res.data) {
            const { id, autoStartToken: at, bankIdQRImage: qr } = res.data.signupWithBankId

            setBankidSessionId(id)
            setAutoStartToken(at)
            setQR(qr)
            if (onAfterSubmit) {
              onAfterSubmit()
            }
          }
        })
      }}
      style={{ width: '100%' }}
    >
      {bankidSessionId ? (
        <div className={styles.bankIdContainer} ref={bankIdRef}>
          <BankidCard
            status={
              <BankidSessionStatus
                autoStartToken={autoStartToken}
                bankIdQRImage={bankIdQRImage}
                onBeforeNavigate={() => {
                  window.location.href = redirectUrl
                }}
                onComplete={(data) => {
                  if (event) {
                    pushGAEvent(event)
                  }
                  const baseURL = `https://go.${getEnv()}.com/login?token=${data.token}`

                  window.location.href = `${baseURL}&billing=true`
                }}
                onFailure={(data?: DooerComGetBankIdSessionV2_bankIdSessionV2 | ApolloError) => {
                  if (isGraphqlError(data)) {
                    setBankidError(data)
                  } else {
                    setBankidSessionData(data)
                  }

                  setBankidSessionId(undefined)
                }}
                redirect={encodeURIComponent(redirectUrl)}
                sessionId={bankidSessionId}
              />
            }
            withSpinner={!autoStartToken}
          />
        </div>
      ) : (
        <>
          <SubTitle className={styles.subTitle} color="gray">
            <FormattedMessage defaultMessage="Ditt företag" id="diy-accounting.signup-form.company-information" />
          </SubTitle>

          <Text
            errorMessage={errorMessages.companyName}
            input={input}
            label={intl.formatMessage(messages.companyName)}
            name="companyName"
            required
            type="text"
            valid={
              formState?.values?.companyName?.length >= 1 && formState?.values?.companyName?.length <= 2
                ? 'invalid'
                : getValidity(formState, 'companyName')
            }
            validate={(value) => value.length > 2}
          />

          <Text
            errorCollapse="dynamic"
            errorMessage={errorMessages.organizationNumber}
            input={input}
            label={intl.formatMessage(messages.organizationNumber)}
            name="orgNr"
            required
            type="text"
            valid={
              !isEmpty(formState?.values?.orgNr) &&
              !validateSvensktOrganisationsnummer.isValid(normalizeSvensktOrganisationsnummer(formState?.values?.orgNr))
                ? 'invalid'
                : getValidity(formState, 'orgNr')
            }
            validate={(value) => validateSvensktOrganisationsnummer.isValid(normalizeSvensktOrganisationsnummer(value))}
          />

          <SubTitle className={styles.subTitle} color="gray">
            <FormattedMessage defaultMessage="Kontaktuppgifter" id="diy-accounting.signup-form.contact-information" />
          </SubTitle>

          <Text
            // SMELL: apollo-boost swallows the error before we can see it, so we can't know for sure that this is the exact error
            errorCollapse="dynamic"
            errorMessage={
              apolloError &&
              apolloError.graphQLErrors &&
              apolloError.graphQLErrors.some((err) => err.extensions && err.extensions.code === 'signup-email-mismatch')
                ? errorMessages.emailMismatch
                : errorMessages.email
            }
            input={input}
            label={intl.formatMessage(messages.email)}
            name="email"
            required
            type="email"
            valid={apolloError ? 'invalid' : getValidity(formState, 'email')}
            validate={(value) => value.includes('@' && '.')}
          />

          <Toggle
            checked={formState.values.integrity}
            errorMessage={errorMessages.integrity}
            input={input}
            label={
              <>
                {intl.formatMessage(messages.integrity)}{' '}
                <a className={styles.nobreak} href="/gdpr" target="_blank">
                  <FormattedMessage {...messages.readMore} />
                </a>
              </>
            }
            name="integrity"
            required
            valid={getValidity(formState, 'integrity')}
          />

          <Toggle
            checked={formState.values.termsOfService}
            errorMessage={errorMessages.termsOfService}
            input={input}
            label={
              <>
                {intl.formatMessage(messages.termsOfService)}{' '}
                <a className={styles.nobreak} href="/tos" target="_blank">
                  <FormattedMessage {...messages.readMore} />
                </a>
              </>
            }
            name="termsOfService"
            required
            valid={getValidity(formState, 'termsOfService')}
          />

          {apolloError && (
            <Error>
              <ApolloErrorMessage error={apolloError} />
            </Error>
          )}
          {bankidSessionData &&
            bankidSessionData.status &&
            bankidSessionData.status === 'failed' &&
            bankidSessionData.hintCode && (
              <Error>
                <FormattedMessage
                  {...getBankidSessionStatusMessage(bankidSessionData.status, bankidSessionData.hintCode)}
                />
              </Error>
            )}

          <Button
            bankId
            block
            className={styles.submit}
            disabled={
              loading || !formState.validity.orgNr || !formState.validity.companyName || !formState.validity.email
            }
            type="submit"
            variant="success"
          >
            <>
              <span className="visible-xs">
                <FormattedMessage {...msgs.submit} />
              </span>
              <span className="hidden-xs">
                <FormattedMessage {...messages.submitShort} />
              </span>
            </>
          </Button>
          <div className={st.handwritingWrapper}>
            <FreeHandwriting text={<FormattedMessage tagName="h3" {...msgs.arrowText} />} variant="left" />
          </div>
        </>
      )}
    </form>
  )
}

export default Form
